import { useMemo } from 'react';
import {
    type GetLocalVariantsOptionsQueryVariables,
    useGetLocalVariantsOptionsQuery,
} from '../../../../../api/queries/getLocalVariantsOptions';
import {
    type ListFinderVehiclesQueryVariables,
    useListFinderVehiclesQuery,
} from '../../../../../api/queries/listFinderVehicles';
import useTranslatedString from '../../../../../utilities/useTranslatedString';

export type RecordType = {
    key: string;
    title: string;
};

export const useFinderVehicles = (filter?: ListFinderVehiclesQueryVariables['filter']) => {
    const translatedString = useTranslatedString();
    const { data: finderVehicleData } = useListFinderVehiclesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter,
        },
    });

    const vehicleDataSource: RecordType[] = useMemo(() => {
        if (!finderVehicleData) {
            return [];
        }

        return finderVehicleData.vehicles.items.map(vehicle => ({
            key: vehicle.versioning.suiteId,
            title: `${translatedString(vehicle.name)} (${vehicle.listing.id})`,
        }));
    }, [finderVehicleData, translatedString]);

    return vehicleDataSource;
};

const useLocalVehicles = (filter?: GetLocalVariantsOptionsQueryVariables['filter']) => {
    const translatedString = useTranslatedString();
    const { data: localVariantData } = useGetLocalVariantsOptionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
    });

    const vehicleDataSource: RecordType[] = useMemo(() => {
        if (!localVariantData) {
            return [];
        }

        return localVariantData.list.items.map(variant => ({
            key: variant.versioning.suiteId,
            title: translatedString(variant.name),
        }));
    }, [localVariantData, translatedString]);

    return vehicleDataSource;
};

export const hasVehicle = (source: RecordType[], key: string) => source.some(i => i.key === key);

export default useLocalVehicles;
